import { WithID } from "../../types/db";
import { Listing, ListingPhoto, ListingReview } from "../../types/db/listings";
import { auth, db } from "../firebase";
import { ListingService } from "../../types/services/listing/ListingService";
import { ListingPhotosService } from "../../types/services/listing/ListingPhotosService";
import { ListingReviewsService } from "../../types/services/listing/ListingReviewsService";

/** The type of information of the listing can be updated from the Front-End */
type TListingUpdate = Pick<Listing, 'title' | 'short_description' | 'description' | 'pricing' | 'guests_restriction' | 'guest_rules' | 'cohost_perk' | 'status'>;

/** The type of information of the listing can be created from the Front-End */
type TListingCreate = Pick<Listing, 'type' | 'host' | 'location' | 'parent_id' | 'duplicate_from'> & TListingUpdate;

/**
 * The Listing Service but for the Front-End (take in consideration the rule restriction of info access read/write)
 */
export class ListingFEService extends ListingService {
    constructor() {
        super(db);
    }

    public listingPhotosService(listingId: string): ListingPhotosService {
        return new ListingPhotosFEService(listingId)
    }

    public listingReviewsService(listingId: string): ListingReviewsService {
        return new ListingReviewsFEService(listingId)
    }

    /**
     * New Listing cannot be created with a specific id (auto generate the id)
     * In the flow of creating a new listing, we can only have partial information
     */
    async create(data: Partial<TListingCreate> & Pick<Listing, 'host'>): Promise<string> {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            console.warn('cannot create listing if user not logged in');
            return Promise.reject("un-authorized");
        }
        if (userId !== data.host.id) {
            return Promise.reject('Trying to create a listing where the user is not the host')
        }
        let newListing = data;
        //if we have a duplicated_from info, we copy the data from the given listing
        // const duplicateFrom = data.duplicate_from;
        // if (!!duplicateFrom) {
        //     newListing = {
        //         ...newListing,
        //         ...await this.get(duplicateFrom)
        //     }
        // }
        return super.create({
            ...newListing,
            status: "pending",//newly created 
        } as Listing)
    }

    /**
     * Update the public listing information
     * @param listingId the listing id
     * @param data the user info to update (can only update certain value from the front-end/by the user)
     * @throws if current user is not allowed to update the listing
     */
    update(listingId: string, data: Partial<TListingUpdate>): Promise<void> {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            console.warn('cannot update listing if user not logged in');
            return Promise.reject("un-authorized");
        }
        // if (auth.currentUser?.uid !== data.host.id) {
        //     return Promise.reject('Trying to update a user data who is not itself')
        // }
        // above check will be done by the rule
        return super.update(listingId, data);
    }

    /**
     * Merge data into the public user information
     * @param listingId the user id
     * @param data the user info to merge into the existing data (can only update certain value from the front-end/by the user)
     * @throws if current user is not the provided id
     * @see update for normal usage for data update
     */
    merge(listingId: string, data: Partial<TListingUpdate>): Promise<void> {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            console.warn('cannot update listing if user not logged in');
            return Promise.reject("un-authorized");
        }
        // if (auth.currentUser?.uid !== data.host.id) {
        //     return Promise.reject('Trying to merge info into a user data who is not itself')
        // }
        return super.merge(listingId, data);//will be rejected by the rule
    }

    /**
     * Listing cannot be deleted from Front-End
     * @param listingId
     * @throws 
     * @see update to update the status instead of deleting
     */
    delete(listingId: string): Promise<void> {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            console.warn('cannot delete listing if user not logged in');
            return Promise.reject("un-authorized");
        }
        // if (auth.currentUser?.uid !== listingId) {
        //     return Promise.reject('Trying to delete a user data who is not itself')
        // }
        return Promise.reject('Error: UNAUTHORIZED - Trying to delete a listing from the Front-End')
    }

    /** Move a listing to a new parent */
    move(listingId: string, newParentId: string) {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            console.warn('cannot move listing if user not logged in');
            return Promise.reject("un-authorized");
        }
        super.update(listingId, { parent_id: newParentId });//the rest is done by back-end trigger TODO
    }

    batchCreate(entities: { data: Listing }[]): Promise<string[]> {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            console.warn('cannot create listings if user not logged in');
            return Promise.reject("un-authorized");
        }
        if (entities.some(entity => entity.data.host?.id !== auth.currentUser?.uid)) {
            return Promise.reject('Error: UNAUTHORIZED - Trying to batch create listings with host not current user from the Front-End');
        }
        return super.batchCreate(entities);
    }
}

/**
 * The Listing Photo Service but for the Front-End (take in consideration the rule restriction of info access read/write)
 */
class ListingPhotosFEService extends ListingPhotosService {
    constructor(protected listingId: string) {
        super(db, listingId);
    }
}

/**
 * The Listing Review Service but for the Front-End (take in consideration the rule restriction of info access read/write)
 */
class ListingReviewsFEService extends ListingReviewsService {
    constructor(protected listingId: string) {
        super(db, listingId);
    }

    create(data: never): Promise<string> {
        //only the backend is allowed to touch this
        return Promise.reject("un-authorized");
    }

    update(id: string, data: never): Promise<void> {
        //only the backend is allowed to touch this
        return Promise.reject("un-authorized");
    }

    delete(id: string): Promise<void> {
        //only the backend is allowed to touch this
        return Promise.reject("un-authorized");
    }

    merge(id: string, data: Partial<ListingReview>): Promise<void> {
        //only the backend is allowed to touch this
        return Promise.reject("un-authorized");
    }

    batchCreate(entities: { id?: string; data: ListingReview; }[]): Promise<string[]> {
        //only the backend is allowed to touch this
        return Promise.reject("un-authorized");
    }
    batchUpdate(updates: { id: string; data: Partial<ListingReview>; }[]): Promise<void> {
        //only the backend is allowed to touch this
        return Promise.reject("un-authorized");
    }
    batchDelete(ids: string[]): Promise<void> {
        //only the backend is allowed to touch this
        return Promise.reject("un-authorized");
    }
    batchMerge(updates: { id: string; data: Partial<ListingReview>; }[]): Promise<void> {
        //only the backend is allowed to touch this
        return Promise.reject("un-authorized");
    }
}

/**
 * Get the photos from the listing
 * @param listing the listing id or a list of listing ids (if we want pictures from listing and its listing parents)
 * @returns the list of photos
 */
export const getListingPhotos = async (
    listing: string | string[],
    ordered = true,
    limit?: number,
): Promise<WithID<ListingPhoto>[]> => {
    if (Array.isArray(listing)) {
        return (await Promise.all(listing.map(l => getListingPhotos(l))))
            .reduce((prev, cur) => {
                return [...prev, ...cur];
            }, []);
    }

    return (new ListingFEService()).listingPhotosService(listing).list(limit);
}

/**
 * Add a photo to the listing (only deals with the data, to add in storage call `addPictureToListing from storage service)
 * @param photo The photo
 * @param listing The listing we want to add it to
 * @returns A Promise resolved with the photo id
 */
// export const addPhotoToListing = async (
//     photoData: Omit<ListingPhoto, 'timestamp'>,
//     listingId: string) => {
//     if (!listingId) return Promise.reject('Listing not defined');

//     return new ListingFEService().listingPhotosService(listingId).create(photoData);
// }

/**
 * Get the review from the listing
 * @param listing the listing id or a list of listing ids (if we want reviews from listing and its listing parents)
 * @returns the list of reviews
 */
export const getListingReviews = async (
    listing: string | string[],
    limit?: number,
): Promise<WithID<ListingReview>[]> => {
    if (Array.isArray(listing)) {
        return (await Promise.all(listing.map(l => getListingReviews(l)))).reduce((prev, cur) => {
            return [...prev, ...cur];
        }, []);
    }

    return (new ListingFEService()).listingReviewsService(listing).list(limit);
}

/** Simple fnuction to indicate if the listing is publishable (ie: minimum criteria to be publishable) */
export const isListingPublishable = (listing?: Partial<Listing> | null) => {
    return !!listing
        && !!listing.title
        && !!listing.short_description
        && !!listing.main_photo
}